import svg from './svg';
import events from './events';

// Try to import the readerStore, but don't fail if it's not available
let useReaderStore;
try {
  // Dynamic import to avoid build errors
  useReaderStore = require('../../../src/store/readerStore').useReaderStore;
} catch (e) {
  // Create a mock readerStore with default values
  useReaderStore = {
    getState: () => ({
      lineSpacing: 1.5,
      fontSize: 16,
    }),
  };
}

export class Pane {
  constructor(target, container = document.body) {
    this.target = target;
    this.element = svg.createElement('svg');
    this.marks = [];

    // Match the coordinates of the target element
    this.element.style.position = 'absolute';
    // Disable pointer events
    this.element.setAttribute('pointer-events', 'none');

    // Set up mouse event proxying between the target element and the marks
    events.proxyMouse(this.target, this.marks);

    this.container = container;
    this.container.appendChild(this.element);

    this.render();
  }

  addMark(mark) {
    var g = svg.createElement('g');
    this.element.appendChild(g);
    mark.bind(g, this.container);

    this.marks.push(mark);

    mark.render();
    return mark;
  }

  removeMark(mark) {
    var idx = this.marks.indexOf(mark);
    if (idx === -1) {
      return;
    }
    var el = mark.unbind();
    this.element.removeChild(el);
    this.marks.splice(idx, 1);
  }

  render() {
    setCoords(this.element, coords(this.target, this.container));
    for (var m of this.marks) {
      m.render();
    }
  }
}

export class Mark {
  constructor() {
    this.element = null;
  }

  bind(element, container) {
    this.element = element;
    this.container = container;
  }

  unbind() {
    var el = this.element;
    this.element = null;
    return el;
  }

  render() {}

  dispatchEvent(e) {
    if (!this.element) return;
    this.element.dispatchEvent(e);
  }

  getBoundingClientRect() {
    return this.element.getBoundingClientRect();
  }

  getClientRects() {
    var rects = [];
    var el = this.element.firstChild;
    while (el) {
      rects.push(el.getBoundingClientRect());
      el = el.nextSibling;
    }
    return rects;
  }

  filteredRanges() {
    if (!this.range) {
      return [];
    }

    const rawRects = Array.from(this.range.getClientRects());
    const textRects = getTextRangeRects(this.range);
    const sourceRects = textRects.length > 0 ? textRects : rawRects;
    const normalized = normalizeHighlightRects(sourceRects);
    this.lastFilteredRangeStats = {
      rawRectCount: rawRects.length,
      textRectCount: textRects.length,
      normalizedRectCount: normalized.length,
      usedTextRects: textRects.length > 0,
      rawArea: getRectsArea(rawRects),
      sourceArea: getRectsArea(sourceRects),
    };
    return normalized;
  }
}

const RECT_EPSILON = 0.5;
const HORIZONTAL_MERGE_GAP = 2;
const TEXT_NODE = 3;
const TEXT_NODE_FILTER = 4;
const FILTER_ACCEPT = 1;
const FILTER_REJECT = 2;
const RANGE_START_TO_END = 1;
const RANGE_END_TO_START = 3;

function getRectsArea(rects) {
  return rects.reduce(
    (sum, rect) => sum + Math.max(0, rect.width || 0) * Math.max(0, rect.height || 0),
    0
  );
}

function rectToPlainObject(rect) {
  const left = Number(rect.left) || 0;
  const top = Number(rect.top) || 0;
  const width = Number(rect.width) || 0;
  const height = Number(rect.height) || 0;
  const right = Number.isFinite(rect.right) ? Number(rect.right) : left + width;
  const bottom = Number.isFinite(rect.bottom) ? Number(rect.bottom) : top + height;

  return {
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
  };
}

function rectKey(rect) {
  return [rect.left, rect.top, rect.right, rect.bottom]
    .map((value) => value.toFixed(2))
    .join(':');
}

function lineOverlapsRect(line, rect) {
  const overlap = Math.min(line.bottom, rect.bottom) - Math.max(line.top, rect.top);
  const minHeight = Math.min(line.bottom - line.top, rect.height);
  if (overlap >= Math.max(RECT_EPSILON, minHeight * 0.5)) {
    return true;
  }

  const lineCenter = (line.top + line.bottom) / 2;
  const rectCenter = (rect.top + rect.bottom) / 2;
  return Math.abs(lineCenter - rectCenter) <= Math.max(RECT_EPSILON, minHeight * 0.35);
}

function rectContainsRect(outer, inner) {
  return (
    inner.left >= outer.left - RECT_EPSILON &&
    inner.right <= outer.right + RECT_EPSILON &&
    inner.top >= outer.top - RECT_EPSILON &&
    inner.bottom <= outer.bottom + RECT_EPSILON
  );
}

function getContainedTextLines(containerRect, rects) {
  const lines = [];
  for (const rect of rects) {
    if (
      rect === containerRect ||
      rect.height >= containerRect.height - RECT_EPSILON ||
      !rectContainsRect(containerRect, rect)
    ) {
      continue;
    }

    const line = lines.find((candidate) => lineOverlapsRect(candidate, rect));
    if (!line) {
      lines.push({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
      });
      continue;
    }

    line.top = Math.min(line.top, rect.top);
    line.bottom = Math.max(line.bottom, rect.bottom);
    line.left = Math.min(line.left, rect.left);
    line.right = Math.max(line.right, rect.right);
  }

  return lines;
}

function isBlockContainerRect(rect, rects) {
  const containedLines = getContainedTextLines(rect, rects);
  if (containedLines.length >= 2) {
    return true;
  }

  if (containedLines.length !== 1) {
    return false;
  }

  const [line] = containedLines;
  const lineWidth = line.right - line.left;
  const extraWidth = rect.width - lineWidth;
  const leftInset = line.left - rect.left;
  const rightInset = rect.right - line.right;
  const largerInset = Math.max(leftInset, rightInset);
  const smallerInset = Math.min(leftInset, rightInset);
  const enoughHorizontalSlack = extraWidth >= Math.max(12, rect.height * 0.5);
  const asymmetricalSlack = largerInset >= smallerInset * 2 + 8;
  const edgeAligned = smallerInset <= RECT_EPSILON * 2;

  return enoughHorizontalSlack && (asymmetricalSlack || edgeAligned);
}

function filterContainerRects(rects) {
  return rects.filter((rect) => !isBlockContainerRect(rect, rects));
}

function getRangeDocument(range) {
  const container = range.commonAncestorContainer;
  if (container?.ownerDocument) {
    return container.ownerDocument;
  }

  return container?.nodeType === 9 ? container : document;
}

function rangeIntersectsTextNode(range, node) {
  if (typeof range.intersectsNode === 'function') {
    try {
      return range.intersectsNode(node);
    } catch (error) {
      return false;
    }
  }

  const doc = getRangeDocument(range);
  if (!doc?.createRange || typeof range.compareBoundaryPoints !== 'function') {
    return false;
  }

  const nodeRange = doc.createRange();
  nodeRange.selectNodeContents(node);
  try {
    return (
      range.compareBoundaryPoints(RANGE_END_TO_START, nodeRange) > 0 &&
      range.compareBoundaryPoints(RANGE_START_TO_END, nodeRange) < 0
    );
  } finally {
    nodeRange.detach?.();
  }
}

function getSelectedTextNodes(range) {
  const commonAncestor = range.commonAncestorContainer;
  if (!commonAncestor) {
    return [];
  }

  if (commonAncestor.nodeType === TEXT_NODE) {
    return rangeIntersectsTextNode(range, commonAncestor) ? [commonAncestor] : [];
  }

  const doc = getRangeDocument(range);
  if (!doc?.createTreeWalker) {
    return [];
  }

  const root = commonAncestor;
  const walker = doc.createTreeWalker(root, TEXT_NODE_FILTER, {
    acceptNode(node) {
      if (!node.nodeValue || !/\S/.test(node.nodeValue)) {
        return FILTER_REJECT;
      }

      return rangeIntersectsTextNode(range, node) ? FILTER_ACCEPT : FILTER_REJECT;
    },
  });
  const nodes = [];
  let node = walker.nextNode();
  while (node) {
    nodes.push(node);
    node = walker.nextNode();
  }
  return nodes;
}

function getSelectedTextOffsets(range, node) {
  let start = 0;
  let end = node.nodeValue?.length || 0;

  if (node === range.startContainer) {
    start = range.startOffset;
  }
  if (node === range.endContainer) {
    end = range.endOffset;
  }

  start = Math.max(0, Math.min(start, node.nodeValue?.length || 0));
  end = Math.max(start, Math.min(end, node.nodeValue?.length || 0));
  return { start, end };
}

function getTextRangeRects(range) {
  const doc = getRangeDocument(range);
  if (!doc?.createRange) {
    return [];
  }

  const rects = [];
  for (const node of getSelectedTextNodes(range)) {
    const { start, end } = getSelectedTextOffsets(range, node);
    const selectedText = node.nodeValue?.slice(start, end) || '';
    if (!/\S/.test(selectedText)) {
      continue;
    }

    const textRange = doc.createRange();
    try {
      textRange.setStart(node, start);
      textRange.setEnd(node, end);
      rects.push(...Array.from(textRange.getClientRects()));
    } catch (error) {
      // Fall back to the browser's original range rectangles below.
    } finally {
      textRange.detach?.();
    }
  }

  return rects;
}

export function normalizeHighlightRects(rects) {
  const unique = [];
  const seen = new Set();

  for (const rect of rects) {
    const normalized = rectToPlainObject(rect);
    if (normalized.width <= RECT_EPSILON || normalized.height <= RECT_EPSILON) {
      continue;
    }

    const key = rectKey(normalized);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    unique.push(normalized);
  }

  const lines = [];
  const textRects = filterContainerRects(unique);
  for (const rect of textRects.sort((a, b) => a.top - b.top || a.left - b.left)) {
    let line = lines.find((candidate) => lineOverlapsRect(candidate, rect));
    if (!line) {
      line = {
        top: rect.top,
        bottom: rect.bottom,
        rects: [],
      };
      lines.push(line);
    }

    line.top = Math.min(line.top, rect.top);
    line.bottom = Math.max(line.bottom, rect.bottom);
    line.rects.push(rect);
  }

  const mergedRects = [];
  for (const line of lines.sort((a, b) => a.top - b.top)) {
    const spans = line.rects
      .slice()
      .sort((a, b) => a.left - b.left || a.right - b.right);

    for (const rect of spans) {
      const previous = mergedRects[mergedRects.length - 1];
      const sameLine =
        previous &&
        lineOverlapsRect(previous, rect) &&
        Math.abs(previous.top - line.top) <= Math.max(RECT_EPSILON, previous.height);
      const touchesPrevious =
        sameLine && rect.left <= previous.right + HORIZONTAL_MERGE_GAP;

      if (touchesPrevious) {
        const left = Math.min(previous.left, rect.left);
        const right = Math.max(previous.right, rect.right);
        const top = Math.min(previous.top, rect.top, line.top);
        const bottom = Math.max(previous.bottom, rect.bottom, line.bottom);
        previous.left = left;
        previous.right = right;
        previous.top = top;
        previous.bottom = bottom;
        previous.width = right - left;
        previous.height = bottom - top;
      } else {
        mergedRects.push({
          left: rect.left,
          top: Math.min(rect.top, line.top),
          right: rect.right,
          bottom: Math.max(rect.bottom, line.bottom),
          width: rect.width,
          height: Math.max(rect.bottom, line.bottom) - Math.min(rect.top, line.top),
        });
      }
    }
  }

  return mergedRects.sort((a, b) => a.top - b.top || a.left - b.left);
}

/**
 * Adjusts the tops and bottoms of rectangles to align them, covering slight gaps or removing slight overlaps.
 * Ensures that large gaps (e.g., the height of a rectangle or more) are not adjusted to prevent highlighting blank lines.
 *
 * @param {Array} rects - Array of rectangle objects with top, bottom, left, right, height, and width properties.
 * @param {number} threshold - Maximum gap or overlap allowed for adjustment.
 * @returns {Array} - Adjusted array of rectangle objects.
 */
function adjustRectangles(rects, threshold) {
  const adjusted = [];
  for (let i = 0; i < rects.length; i++) {
    if (i === 0) {
      adjusted.push({ ...rects[i] });
      continue;
    }
    const prev = adjusted[adjusted.length - 1];
    const current = rects[i];
    const gap = current.top - prev.top - prev.height;
    const overlap = prev.top + prev.height - current.top;

    if (gap >= 0 && gap < threshold) {
      // Cover the slight gap by extending the previous rectangle
      adjusted[adjusted.length - 1].height += gap;
    } else if (overlap > 0 && overlap < threshold) {
      // Remove the slight overlap by adjusting the previous rectangle's height
      adjusted[adjusted.length - 1].height -= overlap;
    }

    adjusted.push({ ...current });
  }
  return adjusted;
}

/**
 * Scales rectangles vertically by growing them from their center point
 * @param {Array} rects - Array of rectangle objects with top, bottom, height properties
 * @param {number} scaleFactor - Factor to scale height by (e.g., 1.2 for 20% increase)
 * @returns {Array} - Array of scaled rectangle objects
 */
function scaleRectanglesVertically(rects, scaleFactor) {
  return rects.map((rect) => {
    const additionalHeight = rect.height * scaleFactor - rect.height;
    const halfAdditionalHeight = additionalHeight / 2;

    return {
      ...rect,
      top: rect.top - halfAdditionalHeight,
      height: rect.height * scaleFactor,
    };
  });
}

export class Highlight extends Mark {
  constructor(range, className, data, attributes) {
    super();
    this.range = range;
    this.className = className;
    this.data = data || {};
    this.attributes = attributes || {};
  }

  bind(element, container) {
    super.bind(element, container);

    for (var attr in this.data) {
      if (this.data.hasOwnProperty(attr)) {
        this.element.dataset[attr] = this.data[attr];
      }
    }

    for (var attr in this.attributes) {
      if (this.attributes.hasOwnProperty(attr)) {
        this.element.setAttribute(attr, this.attributes[attr]);
      }
    }

    if (this.className) {
      this.element.classList.add(this.className);
    }
  }

  /**
   * Renders the highlight by creating and appending SVG rectangles.
   * Adjusts rectangle positions to align tops and bottoms, avoiding large gaps.
   */
  render() {
    // Empty element
    while (this.element.firstChild) {
      this.element.removeChild(this.element.firstChild);
    }

    const docFrag = this.element.ownerDocument.createDocumentFragment();
    let filtered = this.filteredRanges().sort(
      (a, b) => a.top - b.top || a.height - b.height || a.left - b.left || a.width - b.width
    );
    const offset = this.element.getBoundingClientRect();
    const container = this.container.getBoundingClientRect();
    const lineSpacing = useReaderStore.getState().lineSpacing;
    const actualFontSize = useReaderStore.getState().fontSize;
    const rangeStats = this.lastFilteredRangeStats || {};
    const sourceAreaReduced =
      rangeStats.usedTextRects &&
      rangeStats.rawArea - rangeStats.sourceArea > Math.max(16, rangeStats.rawArea * 0.05);

    if (rangeStats.rawRectCount !== filtered.length || sourceAreaReduced) {
      console.debug('[marks-pane] highlight rects normalized', {
        dataId: this.data?.id || null,
        rawRectCount: rangeStats.rawRectCount,
        textRectCount: rangeStats.textRectCount,
        normalizedRectCount: filtered.length,
        usedTextRects: rangeStats.usedTextRects,
        rawArea: rangeStats.rawArea,
        sourceArea: rangeStats.sourceArea,
        normalizedRects: filtered.slice(0, 12),
      });
    }

    // Calculate line spacing if actualFontSize is available but lineSpacing is not set
    // if (actualFontSize && !lineSpacing && filtered.length > 1) {
    //   const uniqueTops = [...new Set(filtered.map((r) => r.top))].sort((a, b) => a - b);
    //   if (uniqueTops.length > 1) {
    //     const topDiff = uniqueTops[1] - uniqueTops[0];
    //     lineSpacing = topDiff / actualFontSize;
    //   }
    // }

    // Adjust rectangles to align tops and bottoms
    const threshold = actualFontSize || 12; // Example threshold based on font size

    // filtered = scaleRectanglesVertically(filtered, 1.2); // Example 20% increase
    filtered = adjustRectangles(filtered, threshold);

    // Final rect rendering
    for (let i = 0, len = filtered.length; i < len; i++) {
      const r = filtered[i];
      const el = svg.createElement('rect');

      let newHeight = r.height;
      let newY = r.top - offset.top + container.top;

      el.setAttribute('x', r.left - offset.left + container.left);
      el.setAttribute('y', newY);
      el.setAttribute('height', newHeight);
      el.setAttribute('width', r.width);

      docFrag.appendChild(el);
    }

    this.element.appendChild(docFrag);
  }
}

export class Underline extends Highlight {
  constructor(range, className, data, attributes) {
    super(range, className, data, attributes);
  }

  render() {
    // Empty element
    while (this.element.firstChild) {
      this.element.removeChild(this.element.firstChild);
    }

    var docFrag = this.element.ownerDocument.createDocumentFragment();
    var filtered = this.filteredRanges();
    var offset = this.element.getBoundingClientRect();
    var container = this.container.getBoundingClientRect();

    for (var i = 0, len = filtered.length; i < len; i++) {
      var r = filtered[i];

      var rect = svg.createElement('rect');
      rect.setAttribute('x', r.left - offset.left + container.left);
      rect.setAttribute('y', r.top - offset.top + container.top);
      rect.setAttribute('height', r.height);
      rect.setAttribute('width', r.width);
      rect.setAttribute('fill', 'none');

      var line = svg.createElement('line');
      line.setAttribute('x1', r.left - offset.left + container.left);
      line.setAttribute('x2', r.left - offset.left + container.left + r.width);
      line.setAttribute('y1', r.top - offset.top + container.top + r.height - 1);
      line.setAttribute('y2', r.top - offset.top + container.top + r.height - 1);

      line.setAttribute('stroke-width', 1);
      line.setAttribute('stroke', 'black'); //TODO: match text color?
      line.setAttribute('stroke-linecap', 'square');

      docFrag.appendChild(rect);

      docFrag.appendChild(line);
    }

    this.element.appendChild(docFrag);
  }
}

function coords(el, container) {
  var offset = container.getBoundingClientRect();
  var rect = el.getBoundingClientRect();

  return {
    top: rect.top - offset.top,
    left: rect.left - offset.left,
    height: el.scrollHeight,
    width: el.scrollWidth,
  };
}

function setCoords(el, coords) {
  el.style.setProperty('top', `${coords.top}px`, 'important');
  el.style.setProperty('left', `${coords.left}px`, 'important');
  el.style.setProperty('height', `${coords.height}px`, 'important');
  el.style.setProperty('width', `${coords.width}px`, 'important');
}

function contains(rect1, rect2) {
  return (
    rect2.right <= rect1.right && rect2.left >= rect1.left && rect2.top >= rect1.top && rect2.bottom <= rect1.bottom
  );
}
